// src/adminDashboard/shared/hooks/useIntersectionObserver.ts
// Hook for lazy loading with Intersection Observer

import { useState, useEffect, useRef } from 'react';

interface UseIntersectionObserverOptions {
    root?: Element | null;
    rootMargin?: string;
    threshold?: number | number[];
}

const useIntersectionObserver = (options: UseIntersectionObserverOptions = {}) => {
    const [isIntersecting, setIsIntersecting] = useState(false);
    const [hasIntersected, setHasIntersected] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry) return;
                setIsIntersecting(entry.isIntersecting);
                if (entry.isIntersecting) {
                    setHasIntersected(true);
                }
            },
            {
                root: options.root || null,
                rootMargin: options.rootMargin || '50px',
                threshold: options.threshold || 0,
            }
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, [options.root, options.rootMargin, options.threshold]);

    return { ref, isIntersecting, hasIntersected };
};

export default useIntersectionObserver;
